// src/pages/admin/integrations/shopify/components/ConnectTab.tsx
import { useState, type FC, type FormEvent, type ReactNode } from 'react';
import { ShopifyInfo, ToastFn } from '../../../types/integrations';
import { CopyRow } from '../../../components/common/StatusPillAndCopyRow';

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

function Steps({ children }: { children: ReactNode }) {
  return (
    <ol className="list-decimal list-inside space-y-1.5 text-sm text-slate-600 [&_b]:text-slate-800 [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs">
      {children}
    </ol>
  );
}

interface Props {
  info: ShopifyInfo;
  onDone: () => void;
  toast: ToastFn;
}

interface ConnectForm {
  shop?: string;
  accessToken?: string;
  apiSecret?: string;
}

const ConnectTab: FC<Props> = ({ info, onDone, toast }) => {
  const [form, setForm] = useState<ConnectForm>({});
  const [busy, setBusy] = useState(false);

  const set =
    (k: keyof ConnectForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    try {
      // const r = await api.connectShopify(form.shop, form.accessToken, form.apiSecret);
      toast(`Connected to ${form.shop ?? 'store'}`);
      onDone();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Connect failed', 'bad');
    } finally {
      setBusy(false);
    }
  };

  const connected = info.status === 'CONNECTED';

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-slate-900">
          {connected ? 'Update connection' : 'Connect'}
        </h2>

        <Steps>
          <li>
            In Shopify admin open{' '}
            <b>Settings → Apps and sales channels → Develop apps</b> and
            create an app (e.g. "WhatsApp").
          </li>
          <li>
            Under <b>Configuration → Admin API scopes</b> tick{' '}
            <code>read_orders</code>, <code>write_orders</code>,{' '}
            <code>read_customers</code>, <code>read_products</code>, and save.
          </li>
          <li>
            Click <b>Install app</b>, then copy the{' '}
            <b>Admin API access token</b> (shown once) and the{' '}
            <b>API secret key</b>.
          </li>
          <li>
            Paste them here. We create the order, checkout and fulfilment
            webhooks for you.
          </li>
        </Steps>

        <form className="flex flex-col gap-4" onSubmit={submit}>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">
              Store address
            </span>
            <input
              required
              value={form.shop ?? ''}
              onChange={set('shop')}
              placeholder="yourstore.myshopify.com"
              className={inputCls}
            />
            <span className="text-xs text-slate-500">
              yourstore.myshopify.com
            </span>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">
              Admin API access token
            </span>
            <input
              required
              type="password"
              autoComplete="off"
              value={form.accessToken ?? ''}
              onChange={set('accessToken')}
              placeholder="shpat_…"
              className={inputCls}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">
              API secret key
            </span>
            <input
              required
              type="password"
              autoComplete="off"
              value={form.apiSecret ?? ''}
              onChange={set('apiSecret')}
              className={inputCls}
            />
            <span className="text-xs text-slate-500">
              Used to check that webhooks really come from Shopify
            </span>
          </label>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? 'Connecting…' : connected ? 'Reconnect' : 'Connect'}
            </button>
          </div>
        </form>
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-slate-900">Webhook</h2>
        <p className="text-sm text-slate-600">
          Shopify webhooks are created automatically when you connect.
          {info.webhookUrl && ' They post to:'}
        </p>
        {info.webhookUrl && (
          <CopyRow
            label="Webhook address"
            value={info.webhookUrl}
            toast={toast}
          />
        )}
      </section>
    </div>
  );
};

export default ConnectTab;